import React, { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { 
  LayoutDashboard, MapPin, Route, Car, 
  Calendar, LogOut, Menu, X, ChevronRight
} from "lucide-react";
import { Button } from "./ui/button";

const navItems = [
  { 
    path: "/admin", 
    label: "Dashboard", 
    icon: LayoutDashboard,
    exact: true 
  },
  { 
    path: "/admin/locations", 
    label: "Locations", 
    icon: MapPin,
    description: "Manage pickup/dropoff points"
  },
  { 
    path: "/admin/routes", 
    label: "Routes", 
    icon: Route,
    description: "Manage transfer routes"
  },
  { 
    path: "/admin/vehicles", 
    label: "Vehicles", 
    icon: Car,
    description: "Manage vehicle types"
  },
  { 
    path: "/admin/bookings", 
    label: "Bookings", 
    icon: Calendar,
    description: "View & manage bookings"
  },
];

export default function AdminLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const adminInfo = JSON.parse(localStorage.getItem("adminInfo") || "{}");

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminInfo");
    navigate("/admin/login");
  };

  // Get current page title
  const currentPage = navItems.find(item => 
    item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path)
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-layout">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-slate-900 transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="admin-sidebar"
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center">
              <Car className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-white text-sm">Kalamata Airport Taxi</h1>
              <p className="text-[10px] text-slate-400">Admin Panel</p>
            </div>
          </div>
          <button 
            className="lg:hidden text-slate-400 hover:text-white"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = item.exact 
              ? location.pathname === item.path 
              : location.pathname.startsWith(item.path) && location.pathname !== "/admin";
            
            // Special case for dashboard - only active when exact match
            const isDashboardActive = item.path === "/admin" && location.pathname === "/admin";
            const finalActive = item.path === "/admin" ? isDashboardActive : isActive;

            return (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all group ${
                  finalActive
                    ? "bg-blue-600 text-white"
                    : "text-slate-400 hover:text-white hover:bg-slate-800"
                }`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <Icon className={`w-5 h-5 ${finalActive ? "text-white" : "text-slate-500 group-hover:text-white"}`} />
                <span className="font-medium text-sm">{item.label}</span>
                {finalActive && (
                  <ChevronRight className="w-4 h-4 ml-auto" />
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* User section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-slate-700 flex items-center justify-center text-white font-medium text-sm">
              {adminInfo.name?.charAt(0)?.toUpperCase() || "A"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {adminInfo.name || "Admin"}
              </p>
              <p className="text-xs text-slate-400 truncate">
                {adminInfo.email || "admin@example.com"}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            className="w-full bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700 hover:text-white"
            data-testid="sidebar-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top header */}
        <header className="h-16 bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="h-full px-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                className="lg:hidden p-2 -ml-2 text-slate-600 hover:text-slate-900"
                onClick={() => setSidebarOpen(true)}
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-5 h-5" />
              </button>
              <div>
                <h2 className="font-semibold text-slate-900">
                  {currentPage?.label || "Admin"}
                </h2>
                {currentPage?.description && (
                  <p className="text-xs text-slate-500">{currentPage.description}</p>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <a
                href="/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-slate-500 hover:text-slate-700"
              >
                View Site →
              </a>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
}
